/**
 * NBU Bank Directory Service
 * Fetches and caches the list of Ukrainian banks from NBU API
 * Used for IBAN bank code (МФО) validation
 */

interface NbuBank {
  GLMFO: number;       // Bank code (МФО / ID НБУ) - 6 digits
  SHORTNAME: string;   // Short name (e.g., "АТ КБ \"ПриватБанк\"")
  FULLNAME: string;    // Full name
  KSTAN: number;       // Status (1 = active)
  WEBSITE?: string;
}

// Static fallback bank list (common Ukrainian banks)
// Used when NBU API is unavailable (e.g., CORS blocked)
const STATIC_BANKS: Record<string, string> = {
  "300119": "АТ \"БАНК АЛЬЯНС\"",
  "300335": "АТ \"Райффайзен Банк\"",
  "300346": "АТ \"СЕНС БАНК\"",
  "300465": "АТ \"Ощадбанк\"",
  "300506": "АТ \"ПЕРШИЙ ІНВЕСТИЦІЙНИЙ БАНК\"",
  "300528": "АТ \"ОТП БАНК\"",
  "300539": "АТ \"ІНГ Банк Україна\"",
  "300584": "АТ \"СІТІБАНК\"",
  "300614": "АТ \"КРЕДІ АГРІКОЛЬ БАНК\"",
  "300647": "АТ \"БАНК \"КЛІРИНГОВИЙ ДІМ\"",
  "300658": "АТ \"ПІРЕУС БАНК МКБ\"",
  "305299": "АТ КБ \"ПриватБанк\"",
  "305749": "АТ \"БАНК КРЕДИТ ДНІПРО\"",
  "306500": "АТ \"АБ \"РАДАБАНК\"",
  "307770": "АТ \"А - БАНК\"",
  "313009": "АТ \"МОТОР-БАНК\"",
  "313582": "АТ \"МетаБанк\"",
  "313849": "АКБ \"ІНДУСТРІАЛБАНК\"",
  "320371": "АТ \"БАНК \"УКРАЇНСЬКИЙ КАПІТАЛ\"",
  "320478": "АБ \"УКРГАЗБАНК\"",
  "320940": "АТ \"АЛЬТБАНК\"",
  "320984": "АТ \"ПРОКРЕДИТ БАНК\"",
  "321723": "АТ \"БТА БАНК\"",
  "322001": "АТ \"УНІВЕРСАЛ БАНК\"",
  "322313": "АТ \"Укрексімбанк\"",
  "322539": "АТ \"ЮНЕКС БАНК\"",
  "322540": "АТ \"КОМІНБАНК\"",
  "325268": "АТ АКБ \"Львів\"",
  "325365": "АТ \"КРЕДОБАНК\"",
  "325990": "АТ \"ОКСІ БАНК\"",
  "328168": "ПАТ \"МТБ БАНК\"",
  "328209": "Акціонерний банк \"Південний\"",
  "331489": "АТ \"Полтава-банк\"",
  "334840": "ПрАТ \"БАНК ФАМІЛЬНИЙ\"",
  "334851": "АТ \"ПУМБ\"",
  "336310": "АТ \"Ідея Банк\"",
  "339050": "АТ \"КРИСТАЛБАНК\"",
  "339500": "АТ \"ТАСКОМБАНК\"",
  "351005": "АТ \"УКРСИББАНК\"",
  "351254": "АТ \"СКАЙ БАНК\"",
  "351607": "АТ \"БАНК \"ГРАНТ\"",
  "353100": "АТ \"Полікомбанк\"",
  "353489": "АТ \"АСВІО БАНК\"",
  "377090": "АТ \"ЄПБ\"",
  "380106": "АТ \"БАНК ТРАСТ-КАПІТАЛ\"",
  "380281": "АТ \"БІЗБАНК\"",
  "380366": "АТ \"НЕКСЕНТ БАНК\"",
  "380441": "АТ \"КРЕДИТВЕСТ БАНК\"",
  "380526": "АТ \"КБ \"ГЛОБУС\"",
  "380548": "АТ \"АГРОПРОСПЕРІС БАНК\"",
  "380582": "АТ \"МІБ\"",
  "380634": "ПуАТ \"КБ \"АКОРДБАНК\"",
  "380645": "АТ \"БАНК 3/4\"",
  "380731": "АТ \"Дойче Банк ДБУ\"",
  "380797": "АТ \"СЕБ КОРПОРАТИВНИЙ БАНК\"",
  "380838": "АТ \"ПРАВЕКС БАНК\"",
  "380883": "АТ \"УБРР\"",
  "380946": "АТ \"БАНК АВАНГАРД\"",
};

// Cache for bank codes
let bankCodesCache: Set<string> | null = null;
let bankNamesCache: Map<string, string> | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Fetch bank directory from NBU API
 * Note: May fail due to CORS in browser - graceful fallback to no bank names
 */
async function fetchBankDirectory(): Promise<NbuBank[]> {
  try {
    console.log("[NBU Bank Directory] Fetching from NBU API...");
    const response = await fetch(
      "https://bank.gov.ua/NBU_BankInfo/get_data_branch_glbank?json",
      {
        method: "GET",
        headers: {
          "Accept": "application/json",
        },
        mode: "cors",
      }
    );
    
    if (!response.ok) {
      throw new Error(`NBU API error: ${response.status}`);
    }
    
    const data = await response.json();
    console.log("[NBU Bank Directory] Successfully fetched", data.length, "banks");
    return data as NbuBank[];
  } catch (error) {
    // CORS error or network error - this is expected in browser
    console.warn("[NBU Bank Directory] Failed to fetch (likely CORS):", error);
    return [];
  }
}

/**
 * Load bank codes into cache
 * Tries NBU API first, falls back to static list if CORS blocked
 */
async function loadBankCodesCache(): Promise<void> {
  const now = Date.now();
  
  // Return if cache is still valid
  if (bankCodesCache && (now - cacheTimestamp) < CACHE_TTL) {
    return;
  }
  
  // Try to fetch from NBU API
  const banks = await fetchBankDirectory();
  
  if (banks.length > 0) {
    // API succeeded - use dynamic data
    bankCodesCache = new Set();
    bankNamesCache = new Map();
    
    for (const bank of banks) {
      // Only include active banks (KSTAN = 1)
      if (bank.KSTAN === 1) {
        // GLMFO is a 6-digit number, convert to string with leading zeros
        const code = String(bank.GLMFO).padStart(6, "0");
        bankCodesCache.add(code);
        bankNamesCache.set(code, bank.SHORTNAME);
      }
    }
    
    cacheTimestamp = now;
    console.log(`[NBU Bank Directory] Loaded ${bankCodesCache.size} active banks from API`);
  } else {
    // API failed (likely CORS) - use static fallback
    bankCodesCache = new Set(Object.keys(STATIC_BANKS));
    bankNamesCache = new Map(Object.entries(STATIC_BANKS));
    cacheTimestamp = now;
    console.log(`[NBU Bank Directory] Using static fallback with ${bankCodesCache.size} banks`);
  }
}

/**
 * Check if a bank code (МФО) exists in NBU registry
 * NOTE: This is informational only - does NOT block validation
 * IBAN with valid checksum is always considered valid, bank check just adds info
 * 
 * @param bankCode 6-digit bank code from IBAN (positions 5-10, i.e. after "UA" + checksum)
 * @returns Object with bank name if found, always valid: true
 */
export async function validateBankCode(bankCode: string): Promise<{
  valid: boolean;
  bankName?: string;
  bankFound: boolean;
}> {
  // Ensure cache is loaded
  await loadBankCodesCache();
  
  if (!bankCodesCache) {
    // If cache failed to load, skip bank lookup (don't block users)
    console.warn("[NBU Bank Directory] Cache not available, skipping bank lookup");
    return { valid: true, bankFound: false };
  }
  
  const normalizedCode = bankCode.padStart(6, "0");
  
  if (bankCodesCache.has(normalizedCode)) {
    return {
      valid: true,
      bankFound: true,
      bankName: bankNamesCache?.get(normalizedCode),
    };
  }
  
  // Bank not found in registry, but IBAN with valid checksum is still valid
  // (NBU registry might not have all valid bank codes)
  return {
    valid: true,
    bankFound: false,
  };
}

/**
 * Extract bank code from IBAN
 * Ukrainian IBAN: UA XX XXXXXX XXXXXXXXXXXXXXXXXXX
 *                    ^^ ^^^^^^
 *                    |  |
 *                    |  Bank code (МФО) - positions 5-10 (0-indexed: 4-9)
 *                    Checksum - positions 3-4
 */
export function extractBankCodeFromIBAN(iban: string): string {
  const cleanIban = iban.replace(/\s/g, "").toUpperCase();
  // Bank code is at positions 4-9 (0-indexed), i.e. characters 5-10
  return cleanIban.substring(4, 10);
}

/**
 * Get bank name by code
 */
export async function getBankNameByCode(bankCode: string): Promise<string | null> {
  await loadBankCodesCache();
  
  if (!bankNamesCache) return null;
  
  const normalizedCode = bankCode.padStart(6, "0");
  return bankNamesCache.get(normalizedCode) || null;
}

/**
 * Preload bank directory cache
 * Call this on app initialization to avoid delay on first IBAN validation
 */
export function preloadBankDirectory(): void {
  loadBankCodesCache().catch(console.error);
}
